import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum STOFFSTROMBILANZ_SORT_OPTIONS {
    CREATION_DATE = 'createdAt',
    START_DATE = 'startDate',
    END_DATE = 'endDate',
}

export interface StoffstrombilanzFile {
    base64Encoding: string;
    fileType: string;
    fileName?: string;
}

export interface StoffstrombilanzPage {
    stoffstrombilanzList: Stoffstrombilanz[];
    totalCount: number;
}

export interface Stoffstrombilanz extends Record<string, unknown> {
    id: string;
    startDate: Date;
    endDate: Date;
    createdAt: Date;
    files: StoffstrombilanzFile[] | null;
    partners: StoffstrombilanzPartner[] | null;
}

interface StoffstrombilanzPartner {
    partnerNummer: string;
    partnerRolle: string;
    partnerName: string;
}

export enum TimePeriod {
    CALENDAR_YEAR = 'CALENDAR_YEAR',
    CUSTOM = 'CUSTOM',
    FINANCIAL_YEAR = 'FINANCIAL_YEAR',
}

// Define interfaces
export interface StoffstrombilanzRequestPeriodDto {
    type: string;
    periodBegin: Date;
    periodEnd: Date;
}

export interface StoffstrombilanzRequest {
    timePeriod: StoffstrombilanzRequestPeriodDto;
    dePartners?: string[];
    wePartners: string[];
}

export interface StoffstrombilanzState {
    request: StoffstrombilanzRequest;
    isLoading: boolean;
    isLoaded: boolean;
    response: Stoffstrombilanz | null;
    stoffstrombilanzenPage: StoffstrombilanzPage;
    sortBy: string;
    selectedPartner: string | undefined;
    isNewStoffstrombilanzError?: boolean;
    isDeleteError?: boolean;
    isDownloadError?: boolean;
    isLoadError?: boolean;
    errorMessage?: string;
    infobaseAvailable?: boolean;
    hasStoffstrombilanzen?: boolean;
    isCheckingHasStoffstrombilanzen?: boolean;
}

// Define the initial state for StoffstrombilanzRequest
const initialState: StoffstrombilanzState = {
    request: {
        timePeriod: {
            type: TimePeriod.CALENDAR_YEAR,
            periodBegin: new Date(),
            periodEnd: new Date(),
        },
        dePartners: [],
        wePartners: [],
    },
    response: {
        id: '',
        startDate: new Date(),
        endDate: new Date(),
        createdAt: new Date(),
        files: [
            {
                base64Encoding: '',
                fileType: 'xlsx',
            },
            {
                base64Encoding: '',
                fileType: 'pdf',
            },
            {
                base64Encoding: '',
                fileType: 'csv',
            },
        ],
        partners: null,
    },
    stoffstrombilanzenPage: {
        stoffstrombilanzList: [],
        totalCount: 0,
    },
    isLoading: false,
    isLoaded: false,
    sortBy: STOFFSTROMBILANZ_SORT_OPTIONS.CREATION_DATE,
    selectedPartner: undefined,
    isNewStoffstrombilanzError: false,
    isDeleteError: false,
    isDownloadError: false,
    isLoadError: false,
    errorMessage: undefined,
    infobaseAvailable: undefined,
    hasStoffstrombilanzen: undefined,
    isCheckingHasStoffstrombilanzen: false,
};

// Create the slice
const stoffstrombilanzSlice = createSlice({
    name: 'stoffstrombilanz', // The name of the slice
    initialState,
    reducers: {
        setTimePeriodType(state, action: PayloadAction<string>) {
            const type = action.payload;
            if (type === TimePeriod.FINANCIAL_YEAR) {
                const year = state.request.timePeriod.periodBegin.getFullYear();
                const periodBegin = createFinancialYearStart(year);
                const periodEnd = createFinancialYearEnd(year);
                return {
                    ...state,
                    request: {
                        ...state.request,
                        timePeriod: { type, periodBegin, periodEnd },
                    },
                };
            }
            return {
                ...state,
                request: {
                    ...state.request,
                    timePeriod: { ...state.request.timePeriod, type },
                },
            };
        },
        setStartDate(state, action: PayloadAction<Date>) {
            const type = state.request.timePeriod.type as TimePeriod;

            if (type === TimePeriod.CALENDAR_YEAR) {
                const year = action.payload.getFullYear();
                const periodBegin = new Date(year, 0, 1, 12, 0, 0, 0);
                const periodEnd = new Date(year, 11, 31, 12, 0, 0, 0);

                console.log('setStartDate CALENDAR_YEAR', {
                    selected: action.payload,
                    year,
                    periodBegin,
                    periodEnd,
                });

                return {
                    ...state,
                    request: {
                        ...state.request,
                        timePeriod: {
                            ...state.request.timePeriod,
                            periodBegin,
                            periodEnd,
                        },
                    },
                };
            }

            if (type === TimePeriod.FINANCIAL_YEAR) {
                const year = action.payload.getFullYear();
                const periodBegin = createFinancialYearStart(year);
                const periodEnd = createFinancialYearEnd(year);
                return {
                    ...state,
                    request: {
                        ...state.request,
                        timePeriod: {
                            ...state.request.timePeriod,
                            periodBegin,
                            periodEnd,
                        },
                    },
                };
            }

            const newState = {
                ...state,
                request: {
                    ...state.request,
                    timePeriod: { ...state.request.timePeriod, periodBegin: action.payload },
                },
            };
            newState.request.timePeriod.periodEnd = getEndDate(
                state.request.timePeriod.periodEnd,
                action.payload,
                state.request.timePeriod.type
            );
            return newState;
        },
        setEndDate(state, action: PayloadAction<Date>) {
            const startDate =
                state.request.timePeriod.periodBegin.getTime() > action.payload.getTime()
                    ? action.payload
                    : state.request.timePeriod.periodBegin;
            return {
                ...state,
                request: {
                    ...state.request,
                    timePeriod: { ...state.request.timePeriod, periodEnd: action.payload, periodBegin: startDate },
                },
            };
        },
        setWePartners(state, action: PayloadAction<string[]>) {
            {
                return { ...state, request: { ...state.request, wePartners: action.payload } };
            }
        },
        sendStoffstrombilanzRequest(state, action: PayloadAction<StoffstrombilanzRequest>) {
            {
                return {
                    ...state,
                    isLoading: true,
                    isDownloadError: false,
                    isDeleteError: false,
                };
            }
        },
        sendStoffstrombilanzRequestSuccess(state, action: PayloadAction<Stoffstrombilanz>) {
            return { ...state, response: action.payload, isLoading: false, isLoaded: true, isNewStoffstrombilanzError: false };
        },
        sendStoffstrombilanzRequestFailure(state, action: PayloadAction<string>) {
            {
                return {
                    ...state,
                    isLoading: false,
                    isLoaded: false,
                    isNewStoffstrombilanzError: true,
                    isDownloadError: false,
                    isDeleteError: false,
                    errorMessage: action.payload,
                };
            }
        },
        resetIsLoaded(state) {
            return { ...initialState, infobaseAvailable: true };
        },
        loadStoffstrombilanzen(
            state,
            action: PayloadAction<{
                number: number;
                size: number;
                sort: string;
                partnerNummer?: string;
            }>
        ) {
            return {
                ...state,
                isLoading: true,
                isLoaded: false,
                selectedPartner: action.payload.partnerNummer,
                isNewStoffstrombilanzError: false,
                isDownloadError: false,
                isDeleteError: false,
                isLoadError: false,
            };
        },
        loadStoffstrombilanzenSuccess(state, action: PayloadAction<StoffstrombilanzPage>) {
            return {
                ...state,
                isLoading: false,
                stoffstrombilanzenPage: action.payload,
            };
        },
        loadStoffstrombilanzenFailure(state, action: PayloadAction) {
            return {
                ...state,
                isLoading: false,
                isNewStoffstrombilanzError: false,
                isDownloadError: false,
                isLoadError: true,
                errorMessage: 'Ihre Stoffstrombilanzen könnten nicht geladen werden. Bitte versuchen Sie es erneut später.',
            };
        },
        deleteStoffstrombilanzSuccess(state, action: PayloadAction<string>) {
            return {
                ...state,
                stoffstrombilanzenPage: {
                    ...state.stoffstrombilanzenPage,
                    stoffstrombilanzList: state.stoffstrombilanzenPage.stoffstrombilanzList.filter((item) => item.id !== action.payload),
                    totalCount: state.stoffstrombilanzenPage.totalCount - 1,
                },
                isLoading: false,
            };
        },
        deleteStoffstrombilanz(state, action: PayloadAction<string>) {
            return {
                ...state,
                isLoading: true,
                isNewStoffstrombilanzError: false,
                isDownloadError: false,
                isDeleteError: false,
            };
        },
        deleteStoffstrombilanzFailure(state) {
            return {
                ...state,
                isLoading: false,
                isNewStoffstrombilanzError: false,
                isDownloadError: false,
                isDeleteError: true,
                errorMessage: 'Ihre Stoffstrombilanzen könnten nicht gelöscht werden. Bitte versuchen Sie erneut später',
            };
        },
        checkInfobaseAvailable(state) {
            return {
                ...state,
                isLoading: true,
            };
        },
        downloadSuccess(state) {
            return {
                ...state,
                isLoading: false,
                isDownloadError: false,
            };
        },
        downloadError(state, action: PayloadAction<string>) {
            return {
                ...state,
                isLoading: false,
                isDownloadError: true,
                errorMessage: action.payload,
            };
        },
        infobaseAvailable(state) {
            return {
                ...state,
                isLoading: false,
                infobaseAvailable: true,
            };
        },
        infobaseNotAvailable(state) {
            return {
                ...state,
                infobaseAvailable: false,
                isLoading: false,
            };
        },
        checkHasStoffstrombilanzen(state) {
            return {
                ...state,
                isCheckingHasStoffstrombilanzen: true,
            };
        },
        checkHasStoffstrombilanzenSuccess(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                hasStoffstrombilanzen: action.payload,
                isCheckingHasStoffstrombilanzen: false,
            };
        },
        checkHasStoffstrombilanzenFailure(state) {
            return {
                ...state,
                hasStoffstrombilanzen: false,
                isCheckingHasStoffstrombilanzen: false,
            };
        },
    },
});

function createFinancialYearStart(year: number): Date {
    return new Date(year, 6, 1, 12, 0, 0, 0);
}

function createFinancialYearEnd(year: number): Date {
    return new Date(year + 1, 5, 30, 12, 0, 0, 0);
}

function getEndDate(endDate: Date, startDate: Date, type: string): Date {
    switch (type) {
        case TimePeriod.CALENDAR_YEAR:
            return endDate || new Date();
        case TimePeriod.CUSTOM:
            return endDate.getTime() < startDate.getTime() ? startDate : endDate;
        case TimePeriod.FINANCIAL_YEAR:
            return createFinancialYearEnd(startDate.getFullYear());
        default:
            return new Date();
    }
}

export const {
    setStartDate,
    setEndDate,
    setWePartners,
    setTimePeriodType,
    sendStoffstrombilanzRequestSuccess,
    sendStoffstrombilanzRequestFailure,
    sendStoffstrombilanzRequest,
    resetIsLoaded,
    loadStoffstrombilanzen,
    loadStoffstrombilanzenSuccess,
    loadStoffstrombilanzenFailure,
    deleteStoffstrombilanzSuccess,
    deleteStoffstrombilanzFailure,
    deleteStoffstrombilanz,
    checkInfobaseAvailable,
    infobaseAvailable,
    infobaseNotAvailable,
    downloadError,
    downloadSuccess,
    checkHasStoffstrombilanzen,
    checkHasStoffstrombilanzenSuccess,
    checkHasStoffstrombilanzenFailure,
} = stoffstrombilanzSlice.actions;
export const stoffstrombilanzReducer = stoffstrombilanzSlice.reducer;
